import { useCallback, useEffect, useRef, useState } from "react";

export type UpdateState = {
  available: boolean;
  apply: () => void;
  serviceWorkerVersion: string | null;
};

export function usePwaUpdate(): UpdateState {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  const [serviceWorkerVersion, setServiceWorkerVersion] = useState<
    string | null
  >(null);
  const reloadAfterUpdate = useRef(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || import.meta.env.DEV) return;
    let registration: ServiceWorkerRegistration | null = null;
    const requestVersion = (): void => {
      navigator.serviceWorker.controller?.postMessage({
        type: "GET_VERSION",
      });
    };
    const onControllerChange = (): void => {
      if (reloadAfterUpdate.current) window.location.reload();
      else requestVersion();
    };
    const onMessage = (
      event: MessageEvent<{ type?: string; version?: string }>,
    ): void => {
      if (event.data?.type === "SW_VERSION")
        setServiceWorkerVersion(event.data.version ?? null);
    };
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      onControllerChange,
    );
    navigator.serviceWorker.addEventListener("message", onMessage);
    void navigator.serviceWorker
      .register("/service-worker.js")
      .then((result) => {
        registration = result;
        if (result.waiting) setWaiting(result.waiting);
        result.addEventListener("updatefound", () => {
          const installing = result.installing;
          installing?.addEventListener("statechange", () => {
            if (
              installing.state === "installed" &&
              navigator.serviceWorker.controller
            )
              setWaiting(result.waiting);
          });
        });
        requestVersion();
        result.active?.postMessage({ type: "GET_VERSION" });
        return result.update();
      });
    const interval = window.setInterval(
      () => void registration?.update(),
      60 * 60 * 1000,
    );
    return () => {
      window.clearInterval(interval);
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        onControllerChange,
      );
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, []);

  const apply = useCallback(() => {
    reloadAfterUpdate.current = true;
    waiting?.postMessage({ type: "SKIP_WAITING" });
  }, [waiting]);
  return { available: waiting !== null, apply, serviceWorkerVersion };
}
